import { type InboxQuery, type MyAccountQuery, useInboxQuery, useMyAccountQuery } from "@qw/graphql";
import { useAtomValue, useSetAtom } from "jotai";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router";
import { authDataAtom, authTokenValidatedAtom, jwtDataAtom } from "../atoms";
import { AuthBootstrap } from "../components/auth-bootstrap";
import { AuthUnreachable } from "../components/auth-unreachable";
import { classifyAuthFailure, isApolloError, isCachedUserStale } from "../utils";
import { useAuthContext } from "./use-auth-context";
import { useCredentialSwapRefetch } from "./use-credential-swap-refetch";

interface AccountContextValue {
  user: NonNullable<MyAccountQuery["myAccount"]>;
  userInbox: InboxQuery["inbox"];
  impersonatorEmail: string | null;
}

const AccountContext = createContext<AccountContextValue | null>(null);

/**
 * One automatic retry, then hand the user the button. A longer ladder hides a real outage behind a
 * spinner, which is the failure mode `AuthBootstrap`'s 15s "stuck" threshold exists to fix.
 */
const AUTO_RETRY_DELAY_MS = 1500;

type RetryState = "idle" | "pending" | "spent";

const GENERIC_ERROR = "We're sorry, an error occurred";
const GENERIC_ERROR_HINT =
  "Retrying may work. If it doesn't, this one is on us — it isn't your connection or your account.";

/**
 * The server's own words for a real GraphQL error, shown rather than swallowed.
 *
 * A preview deployment running a client against a skewed schema fails here, and the generic
 * headline alone loses the one fact that identifies it — which field was rejected. Same content
 * `AuthError` already renders on the login form for any GraphQL error, so this is the established
 * boundary for this text, not a new one.
 *
 * Both transports, because a schema-skew 400 uses the second one. Apollo only populates
 * `graphQLErrors` when the response was a 2xx; for a non-2xx it throws `ServerError` and the body's
 * errors end up on `networkError.result.errors`, with `graphQLErrors` empty — reading only the
 * first would lose exactly the case this exists for. A `networkError` *message* is still left out:
 * it is transport noise, and this branch is only reached when the failure is neither a sign-out nor
 * an outage.
 */
const graphQLErrorDetail = (error: unknown): string | undefined => {
  if (!isApolloError(error)) {
    return undefined;
  }

  const result = (error.networkError as { result?: unknown } | null | undefined)?.result;
  const bodyErrors =
    result && typeof result === "object" && Array.isArray((result as { errors?: unknown }).errors)
      ? ((result as { errors: { message?: unknown }[] }).errors ?? [])
      : [];

  const messages = [...error.graphQLErrors, ...bodyErrors]
    .map(({ message }) => (typeof message === "string" ? message.trim() : ""))
    .filter(Boolean);

  return messages.length > 0 ? [...new Set(messages)].join(", ") : undefined;
};

export const AccountProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    data: { myAccount: user } = {},
    loading,
    error,
    refetch,
  } = useMyAccountQuery({
    fetchPolicy: "cache-and-network",
  });

  const {
    data,
    error: inboxError,
    refetch: refetchInbox,
  } = useInboxQuery({
    fetchPolicy: "cache-and-network",
  });

  const jwtPayload = useAtomValue(jwtDataAtom);
  const ldClient = useLDClient();
  const authData = useAtomValue(authDataAtom);
  const setAuthData = useSetAtom(authDataAtom);
  const setTokenValidated = useSetAtom(authTokenValidatedAtom);
  const { onAuthFailure } = useAuthContext();

  // `currentToken` is what stops this component from being the second sign-out authority that
  // undoes the link's staleness check: a 401 raised by a request carrying a credential another tab
  // has since replaced reads as `unreachable` here, so `authDataAtom` keeps the new one.
  const currentToken = authData?.token.accessToken;
  // Both memoized to a *kind* rather than recomputed inside their effects. `currentToken` changes
  // once on mount — jotai snapshots `atomWithStorage`'s base value at import and only reads storage
  // in `onMount` — so an effect depending on it directly fires twice per failure and doubles the
  // reported count.
  const failure = useMemo(() => (error ? classifyAuthFailure(error, { currentToken }) : null), [error, currentToken]);
  const inboxFailure = useMemo(
    () => (inboxError ? classifyAuthFailure(inboxError, { currentToken }) : null),
    [inboxError, currentToken]
  );
  const [retryState, setRetryState] = useState<RetryState>("idle");
  const autoRetryScheduled = useRef(false);
  const autoRetryTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // These are read through refs so the effects below depend only on the *failure*, not on a
  // callback identity. Apollo rebuilds `refetch` on every render (`observable.refetch.bind(...)`),
  // so a dependency on it would re-run the retry effect on every render — and the effect's cleanup
  // would cancel the pending timer each time, leaving the app on the bootstrap screen for good.
  // The same dependency on `onAuthFailure` would emit a duplicate report per render for any app
  // that passes an inline handler.
  const refetchRef = useRef(refetch);
  const refetchInboxRef = useRef(refetchInbox);
  const onAuthFailureRef = useRef(onAuthFailure);

  useEffect(() => {
    refetchRef.current = refetch;
    refetchInboxRef.current = refetchInbox;
    onAuthFailureRef.current = onAuthFailure;
  }, [onAuthFailure, refetch, refetchInbox]);

  // `async` rather than `.catch()`, so this can neither reject nor throw synchronously: the
  // automatic retry's settle handler is what moves the render off the bootstrap, and an escaping
  // error there would strand the app on it. Apollo's `refetch` rejects whenever the query errors,
  // which is exactly when this is called.
  //
  // Retries both queries, so one attempt fixes whichever of them failed. `Inbox` is not gating the
  // tree, but a missing `userInbox` is not visibly different from an empty one.
  const retry = useCallback(async () => {
    try {
      await Promise.allSettled([refetchRef.current(), refetchInboxRef.current()]);
    } catch {
      // `allSettled` cannot reject, so this only catches a refetch that threw synchronously. The
      // failure is already in `error`; the caller only needs to know the attempt is over.
    }
  }, []);

  /** Shared with both `AccountProviderNextJs` copies — see the helper for why and how. */
  const cachedUserIsStale = isCachedUserStale({ jwtPayload, user });

  useEffect(() => {
    if (!user || !ldClient || cachedUserIsStale) {
      return;
    }

    void ldClient.identify({
      key: user.email,
      id: user.id,
      email: user.email,
    });
  }, [user, ldClient, cachedUserIsStale]);

  // Pull the account for whoever the stored credential now belongs to — and, when that pull fails,
  // report `failed` rather than waiting forever. Shared with both `AccountProviderNextJs` copies.
  const { retry: retrySwap, status: swapStatus } = useCredentialSwapRefetch({
    isStale: cachedUserIsStale,
    refetch: retry,
  });

  useEffect(() => {
    if (!failure) {
      return;
    }

    onAuthFailureRef.current?.({ kind: failure, operation: "MyAccount" });
  }, [failure]);

  // Reported, not rendered. A failed `Inbox` fetch leaves `userInbox` undefined, which every
  // consumer reads as "this user has no inbox" rather than "we could not load it" — so the one
  // thing it must not be is silent. It does not gate the tree: the surfaces that need `userInbox`
  // degrade, the rest are unaffected, and blanking all of them would be the worse trade.
  useEffect(() => {
    if (!inboxFailure) {
      return;
    }

    onAuthFailureRef.current?.({ kind: inboxFailure, operation: "Inbox" });
  }, [inboxFailure]);

  // Clearing the credential is reserved for `signed-out`. On any other failure the token is
  // untested rather than rejected, and nulling `authDataAtom` on that reading is both what sent
  // people to a bare login form holding a token that was fine, and what `AuthGuard` reads as a
  // logged-out transition.
  useEffect(() => {
    if (failure !== "signed-out") {
      return;
    }

    setAuthData(null);
    setTokenValidated(false);
  }, [failure, setAuthData, setTokenValidated]);

  useEffect(() => () => clearTimeout(autoRetryTimer.current), []);

  /**
   * One automatic retry per mount, for an unreachable reading of *either* query.
   *
   * `Inbox` needs it as much as `MyAccount` does, and for a worse reason: an Inbox-only failure
   * never gates the tree, so the user is never offered the Retry button either — leaving a
   * silently-empty inbox until a reload, which is the exact state the failure report exists to
   * stop being silent about. `retry()` refetches both, so one timer covers both.
   *
   * Only a `MyAccount` failure moves the render to the bootstrap: `retryState` exists to gate the
   * account-dependent tree, and an Inbox-only retry has to happen in the background while the app
   * keeps rendering.
   *
   * The latch and the timer are refs, and the timer is cancelled on unmount only — deliberately not
   * when `failure` changes. Settling is what moves the render off the bootstrap, so a cancelled
   * timer could strand the app on it; and a cleanup keyed on the state this effect writes would
   * cancel the timer one render after scheduling it.
   */
  const shouldAutoRetry = failure === "unreachable" || inboxFailure === "unreachable";

  useEffect(() => {
    if (!shouldAutoRetry || autoRetryScheduled.current) {
      return;
    }

    autoRetryScheduled.current = true;

    if (failure === "unreachable") {
      setRetryState("pending");
    }

    autoRetryTimer.current = setTimeout(() => {
      void retry().finally(() => setRetryState("spent"));
    }, AUTO_RETRY_DELAY_MS);
  }, [failure, retry, shouldAutoRetry]);

  // Blank on purpose: the effect above nulls `authDataAtom`, which hands off to `AuthGuard`'s login
  // redirect on the next commit. Painting the bootstrap here would flash on every sign-out.
  if (failure === "signed-out") {
    return null;
  }

  // A usable account beats a failed refetch. This is the same intent as the `!user` gate that used
  // to sit on `loading` alone — an impersonation swap refetches `MyAccount` — now applied to
  // `error` as well, because `error` was checked *first*: one failed background refetch replaced a
  // fully working app that already had an account to show with a bare error heading. "Usable" also
  // means "belongs to the credential we would send" — see `cachedUserIsStale`.
  if (user && !cachedUserIsStale) {
    const value = {
      user,
      userInbox: data?.inbox,
      impersonatorEmail: jwtPayload?.impersonatedByEmail || null,
    };

    return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
  }

  if (loading || retryState === "pending") {
    return <AuthBootstrap />;
  }

  if (failure === "unreachable") {
    return <AuthUnreachable onRetry={retry} />;
  }

  if (error) {
    // biome-ignore lint/suspicious/noConsole: intentional
    console.error(error);
    return (
      <AuthUnreachable hint={graphQLErrorDetail(error) ?? GENERIC_ERROR_HINT} message={GENERIC_ERROR} onRetry={retry} />
    );
  }

  // The credential was swapped by another tab. Wait while the refetch is in flight rather than
  // render the wrong person — but never wait on an attempt that has already failed, which is how
  // this branch used to strand the app on a screen with no exit.
  if (swapStatus === "refetching") {
    return <AuthBootstrap />;
  }

  if (swapStatus === "failed") {
    return (
      <AuthUnreachable
        hint="Your session changed in another tab and we couldn't load the new one."
        message="We couldn't finish switching accounts."
        onRetry={() => {
          retrySwap();
          return retry();
        }}
      />
    );
  }

  // `myAccount: UserAccount!` is non-null, so a response that resolves always carries a user and
  // this is unreachable in practice. Kept as the fallthrough rather than assumed away.
  return <Navigate to="/login" replace />;
};

export const useAccount = () => {
  const context = useContext(AccountContext);

  if (!context) {
    throw new Error("useAccount must be used within an AccountProvider");
  }

  return context;
};
